import React, { Suspense } from "react"
import { BlitzPage } from "@blitzjs/next"
import { useMutation } from "@blitzjs/rpc"
import { useSession } from "@blitzjs/auth"
import { useRouter } from "next/router"
import Loading from "app/core/components/Loading"
import { BookingLayout } from "app/core/layouts/BookingLayout"
import Form, { FORM_ERROR } from "app/core/components/Form"
import LabeledCheckboxField from "app/core/components/LabeledCheckboxField"
import LabeledTextField from "app/core/components/LabeledTextField"
import { Button } from "app/core/components/Button"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import { ButtonLink } from "app/core/components/ButtonLink"
import CompactRadioGroupField from "app/core/components/CompactRadioGroupField"
import { StartNewBookingInput } from "app/bookings/validations"
import { HiArrowRight } from "react-icons/hi"
import startNewBooking from "app/bookings/mutations/startNewBooking"
import { z } from "zod"
import { useErrorHandler } from "react-error-boundary"
import Spinner from "react-md-spinner"

import style from "app/bookings/bookings.module.css"
import { Tracking } from "app/core/tracking"

export const InitialUserDetails = () => {
  const user = useCurrentUser()
  const router = useRouter()

  if (!user) {
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-bold">Start With Your Email</h2>
        <div className="flex flex-col space-y-5 md:flex-row md:space-y-0 md:space-x-5">
          <div className="flex-1 space-y-3 ">
            <LabeledTextField
              name="email"
              label="Your Email"
              hiddenLabel={true}
              placeholder="Your email"
              type="email"
            />
          </div>
          <div className="flex items-center self-end flex-1 space-x-5">
            <div>Or</div>
            <ButtonLink
              color="gray"
              href={{ pathname: "/login", query: { next: router.asPath } }}
              className="w-full px-7 md:w-auto"
            >
              Log In
            </ButtonLink>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <h2 className="text-2xl font-bold">
        {user?.organization.firstName
          ? `Welcome Back, ${user?.organization.firstName}!`
          : "Welcome Back!"}
      </h2>
    </>
  )
}

const BookingTypeForm = () => {
  const session = useSession({ suspense: false })
  const router = useRouter()
  const user = useCurrentUser()
  const [startNewBookingMutation] = useMutation(startNewBooking)
  const handleError = useErrorHandler()

  const schema = session.userId
    ? StartNewBookingInput
    : StartNewBookingInput.extend({
        email: z.string().email(),
      })

  const bookingTypes = [
    {
      label: "I'm a homeschool parent booking for my children",
      value: "homeschool",
    },
    {
      label: "I'm an admin or tutor booking group testing",
      value: "group",
    },
    {
      label: "I'm the admin/head of a private school",
      value: "privateschool",
    },
  ]

  return (
    <Form
      schema={schema}
      onSubmit={async (values) => {
        try {
          if (values.email) {
            Tracking.setEmail(values.email)
          }
          if (values.bookingType === "privateschool") {
            router.push("/book-testing/privateschool")
          } else {
            await startNewBookingMutation(values)
            if (values.email)
              router.push(`/book-testing/${values.bookingType}/new/1?guest=${values.email}`)
            else router.push(`/book-testing/${values.bookingType}/new/1`)
          }
        } catch (error) {
          if (error.name === "AuthenticationError") {
            handleError(error)
          }
          return {
            [FORM_ERROR]:
              "Sorry, we had an unexpected error. Please try again. - " + error.toString(),
          }
        }
      }}
      className="max-w-xl mx-auto"
    >
      {(form) => (
        <div className={style["bookings-container"]}>
          <Suspense fallback={<Loading />}>
            <InitialUserDetails />
          </Suspense>

          <CompactRadioGroupField name="bookingType" label="Booking Type" items={bookingTypes} />

          <LabeledCheckboxField
            name="agreeToTestInUS"
            label="All students will be in the U.S. at time of testing"
          />

          <Button disabled={form.invalid} className="px-5 md:self-end">
            {form.submitting ? (
              <Spinner size={26} singleColor="#ffffff" />
            ) : (
              <>
                Next <HiArrowRight className="mt-px ml-3" />
              </>
            )}
          </Button>
        </div>
      )}
    </Form>
  )
}

const BookTestingPage: BlitzPage = () => {
  return (
    <Suspense fallback={<div />}>
      <BookingTypeForm />
    </Suspense>
  )
}

BookTestingPage.getLayout = (page) => (
  <BookingLayout title="Book NWEA MAP Growth Testing Online">{page}</BookingLayout>
)

export default BookTestingPage
